import { useEffect, useState, useRef, useMemo } from "react";
import Swal from "sweetalert2";
import { Tag, Popover, Button, Form, Tooltip, Popconfirm, Checkbox } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import TextInput from "../../../components/TextInput/TextInput";
import TextAreaInput from "../../../components/TextArea/TextArea";
import UploadButton from "../../../components/UploadButton/RectangleUploadButton";
import Modal from "../../../components/Modal/Modal"
import { useNavigate } from "react-router-dom";
import { ReactComponent as ViewIcon } from "../../../svg/ViewIcon.svg";
import { useStore } from "../../../root-store-context";
import { observer } from "mobx-react-lite";
import { formatDate } from "../../../Common";
import SideBar from "../../../components/sideBar/Sidebar";
import Spinner from "../../../components/Spinner/Spiner";
import DynamicTable from '../../../components/Table/Table';
import EditFormSubmission from "./ViewForm";
import Validation from "../../../utils/Validation";
import FileHelper from "../../../helpers/FileHelper";
import Constants from "./ARCConstant";
import CommonFuncitons from "../../../utils/CommonFunciton";
import "./ArchitecturalRequestList.css"

const ArchitecturalRequestList = observer(() => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [filterListInput] = Form.useForm();
    const { apiStore, applicationStore } = useStore();
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [editFormOpen, setEditFormOpen] = useState(false);
    const formRef = useRef(null);
    const [viewFormData, setViewFormData] = useState();
    const [formDetail, setFormDetail] = useState();
    const [copyopen, setCopyOpen] = useState(false);
    const [showPaymentSection, setShowPaymentSection] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [fileList, setFileList] = useState([]);

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 25,
        total: 0,
    });
    const debounceTimer = useRef(null);
    const [searchTerm, setSearchTerm] = useState(null);
    const [appealOpen, setAppealOpen] = useState(false);
    const [selectedSubmissionDetail, setSelectedSubmissionDetail] = useState({});
    const [appealDetails, setAppealDetails] = useState({});
    const [statusFilter, setStatusFilter] = useState(null);
    const [decisionFilter, setDecisionFilter] = useState(null);
    const [deniedBlockList, setDeniedBlockList] = useState([]);
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const [withdrawOpen, setWithdrawOpen] = useState(false);
    const [withdrawLoading, setWithdrawLoading] = useState(false);
    const [selectedWithdrawSubmission, setSelectedWithdrawSubmission] = useState(null);
    const [withdrawForm] = Form.useForm();

    const validationRules = Validation.getValidation([
        "reason",
        "dayTimePhone",
        "groupSelection"
    ]);

    const confirm = async (id, reason = "") => {
        return await apiStore.FormsApiClient.WithdrawSubmission(
            id?.submissionId,
            apiStore?.configApi?.community?.residentKey?.toString(),
            apiStore?.configApi?.community?.residentContactKey
                ? apiStore?.configApi?.community?.residentContactKey?.toString()
                : null,
            reason,
        ).then((res) => {
            if (res?.data?.status) {
                let data = {
                    pageNumber: 1,
                    pageSize: 10,
                    searchTerm: filterListInput?.getFieldValue("search"),
                    submissionStatus: applicationStore?.submissionStatusString,
                    submissionDecision: applicationStore?.Pagination?.submissionDecision
                };

                fetchData(data);
                Swal.fire({
                    title: "Success",
                    text: "Request withdrawn successfully.",
                    icon: "success",
                    confirmButtonText: "Ok",
                    confirmButtonColor: "#44637e",
                    allowOutsideClick: false,
                });

                return true;
            } else {
                Swal.fire({
                    text: res?.data?.message || "Unable to withdraw request.",
                    icon: "error",
                    confirmButtonColor: "#44637e",
                });
                handleWithdrawCancel();
                return false;
            }
        })
            .catch((err) => {
                console.log("Error in GetArchForm", err);
                Swal.fire({
                    title: "Error",
                    text: err?.message || "Unable to withdraw request.",
                    icon: "error",
                    confirmButtonColor: "#44637e",
                });
                return false;
            });
    };
    const handleWithdrawOpen = (user, e) => {
        e?.stopPropagation?.();
        setSelectedWithdrawSubmission(user);
        withdrawForm?.setFieldsValue({ withdrawReason: "" });
        setWithdrawOpen(true);
    };
    const handleWithdrawCancel = () => {
        setWithdrawOpen(false);
        setSelectedWithdrawSubmission(null);
        withdrawForm?.resetFields();
    };
    const handleWithdrawSubmit = async (values) => {
        setWithdrawLoading(true);
        const isSuccess = await confirm(selectedWithdrawSubmission, values?.withdrawReason);
        setWithdrawLoading(false);
        if (isSuccess) {
            handleWithdrawCancel();
        }
    };

    const renderAction = (user) => {


        return (
            <div className="d-flex action-wraper">
                <Popover placement="topLeft" title={"View Form"}>
                    <Button
                        onClick={() => handleView(user)}
                        className="icon-wrap"
                        shape={"circle"}
                        icon={
                            <ViewIcon style={{ color: '#44637e' }} />
                        }
                    ></Button>
                </Popover>
                {user?.isAbleToWithdraw &&
                    <Popconfirm
                        title="Are You Sure"
                        description="Are you sure you want to withdraw this submission? This action cannot be undone."
                        onConfirm={(e) => handleWithdrawOpen(user, e)}
                        onCancel={(e) => e?.stopPropagation?.()}
                        icon={<QuestionCircleOutlined style={{ color: "red" }} />}
                    >
                        <Button type="primary" className="action-button"
                            onClick={(e) => e.stopPropagation()} >
                            Withdraw </Button>
                    </Popconfirm>
                }
            </div>
        );
    };

    useEffect(() => {
        const existingFilter = applicationStore?.Pagination?.submissionStatus;
        const existingDecisionFilter = applicationStore?.Pagination?.submissionDecision;
        setStatusFilter(existingFilter);
        setDecisionFilter(existingDecisionFilter)
        if (applicationStore?.Pagination) {
            filterListInput?.setFieldsValue({ search: applicationStore?.Pagination?.filter })
        }
    }, []);

    const handleTableChange = (pagination, filters, sorter) => {
        setStatusFilter(filters?.submissionStatus);
        setDecisionFilter(filters?.submissionDecision)
        const { order, field } = sorter;
        const sortOrder = order === "ascend" ? "ASC" : order === "descend" ? "DESC" : null;
        if (sortOrder && field) {
            let data = {
                pageNumber: pagination?.current,
                pageSize: pagination?.pageSize,
                searchTerm: filterListInput?.getFieldValue("search"),
                field: field,
                order: sortOrder,
                submissionStatus: filters?.submissionStatus,
                submissionDecision: filters?.submissionDecision,
            }
            setPagination(pagination);
            applicationStore.setPagination({
                ...applicationStore.Pagination,
                pageSize: pagination?.pageSize,
                current: pagination?.current,
                submissionStatus: filters?.submissionStatus,
                submissionDecision: filters?.submissionDecision,
                field: field,
                order: sortOrder,
            })
            fetchData(data);
        } else {
            let data = {
                pageNumber: pagination?.current,
                pageSize: pagination?.pageSize,
                searchTerm: filterListInput?.getFieldValue("search"),
                submissionStatus: filters?.submissionStatus,
                field: field,
                order: sortOrder,
                submissionDecision: filters?.submissionDecision,
            }
            setPagination(pagination);
            applicationStore.setPagination({
                ...applicationStore.Pagination,
                pageSize: pagination?.pageSize,
                current: pagination?.current,
                submissionStatus: filters?.submissionStatus,
                submissionDecision: filters?.submissionDecision,
            })
            fetchData(data);
        }
    };
    const columns = useMemo(
        () => [
            {
                title: "Request Date",
                dataIndex: "submittedDate",
                width: 190,
                className: "dynamic-table-nowrap-cell",
                render: (value) => formatDate(value),
                sorter: (a, b) => new Date(a.submittedDate) - new Date(b.submittedDate),
                defaultSortOrder: "descend",
                sortDirections: ['ascend', 'descend', 'ascend'],
            },
            {
                title: "Request Number",
                dataIndex: "requestNumber",
                width: 120,
            },
            {
                title: "Submission Type", dataIndex: "submissionType", render: (text) => text || "-",
                width: 120,
                ellipsis: true,
            },
            {
                title: "Status",
                dataIndex: "submissionStatus",
                ...CommonFuncitons.statusTagWidth,
                widthText: (value) => FileHelper.getDisplaySubmissionStatus(value),
                filters: FileHelper.submissionStatusFilter,
                filteredValue: Array.isArray(statusFilter) && statusFilter.length > 0 ? statusFilter : [],
                filterIcon: (filtered) => (
                    <Tooltip title="Click to filter by submission status">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M14.6666 2H1.33325L6.66658 8.30667V12.6667L9.33325 14V8.30667L14.6666 2Z" stroke="#5B5B5B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </Tooltip>
                ),
                render: (value) => {
                    const status = FileHelper.getDisplaySubmissionStatus(value);

                    return status ? (
                        <Tag className={status.className}>{status}</Tag>
                    ) : (
                        '-'
                    );
                },

            },
            {
                title: "Decision",
                dataIndex: "submissionDecision",
                ...CommonFuncitons.statusTagColumn({
                    isBlank: (record) =>
                        record?.submissionStatus === CommonFuncitons.SubmissionStatus.Canceled,
                }),
                filters: FileHelper.decisionStatusFilter,
                filteredValue: Array.isArray(decisionFilter) && decisionFilter.length > 0 ? decisionFilter : [],

                filterIcon: (filtered) => (
                    <Tooltip title="Click to filter by submission status">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M14.6666 2H1.33325L6.66658 8.30667V12.6667L9.33325 14V8.30667L14.6666 2Z" stroke="#5B5B5B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </Tooltip>
                ),
            },
            {
                title: "Action Date",
                dataIndex: "actionDate",
                width: 140,
                render: (value) => formatDate(value),
            },
            {
                title: "Status Notes",
                dataIndex: "reason",
                width: 200,
                ellipsis: true,
                render: (text, record) => {
                    const displayText = FileHelper.committeeStatuses.includes(record?.submissionStatus) ? Constants.SUBMISSION_NOTE
                        : text;
                    const truncatedText = displayText?.length > 100 ? `${displayText?.slice(0, 100)}...` : displayText;

                    return (
                        <Tooltip
                            title={<div style={{ maxWidth: 800 }}>{displayText}</div>}
                            overlayStyle={{ maxWidth: 800 }}
                        >
                            <span>{truncatedText}</span>
                        </Tooltip>
                    );
                },
            },
            {
                title: "Actions",
                width: 150,
                dataIndex: "formName",
                render: (text, record) => renderAction(record),
            },
        ],
        [result, statusFilter, decisionFilter]
    );

    const handleView = (user) => {
        navigate(`/AccountInfo/ArchitecturalRequestsNew/View/${user?.submissionId}`);
    };
    const EditOk = () => {
        setEditFormOpen(!editFormOpen);
    };
    const EditCancel = () => {
        setEditFormOpen(!editFormOpen);
    };
    const CopyOk = () => {
        setCopyOpen(!copyopen);
    };
    const CopyCancel = () => {
        setCopyOpen(!copyopen);
        setShowPaymentSection(false);
    };

    const EditForm = (pageName) => {
        return (
            <EditFormSubmission
                editFormOpen={editFormOpen}
                setEditFormOpen={setEditFormOpen}
                Edit={"edit"}
                GetForm={viewFormData}
                submissionId={viewFormData?.submissionId}
                components={
                    formDetail
                        ?.components
                }
                unitId={viewFormData?.unitId}
                setIsSuccess={setIsSuccess}
            />
        );
    };
    const CopyPermissionsFields = () => {
        return (
            <EditFormSubmission
                editFormOpen={copyopen}
                setEditFormOpen={setCopyOpen}
                Edit={"copy"}
                GetForm={viewFormData}
                submissionId={viewFormData?.submissionId}
                components={
                    formDetail
                        ?.components
                }
                showPaymentSection={showPaymentSection}
                setShowPaymentSection={setShowPaymentSection}
                unitId={formDetail?.unitId}
                setIsSuccess={setIsSuccess}
            />
        );
    };
    const fetchData = async (data) => {
        setLoading(true);
        const submissionStatusString = data?.submissionStatus?.join(",");
        const submissionDecisionString = data?.submissionDecision?.join(",");
        const request = {
            residentKey: [apiStore?.configApi?.community?.residentKey?.toString()], // ["104046"] ,//
            pageNumber: JSON.stringify(data?.pageNumber),
            pageSize: JSON.stringify(data?.pageSize),
            searchKey: data?.searchTerm,
            startDate: null,
            endDate: null,
            submissionStatus: submissionStatusString,
            sortOrder: data?.order ?? "DESC",
            submissionDecision: submissionDecisionString,

        };
        try {
            const response = await apiStore.FormsApiClient.GetRequestList(JSON.stringify(request));

            setLoading(false);

            if (response?.data?.status) {
                setResult(response?.data?.data);
                applicationStore?.setRequestList(response?.data?.data)
                applicationStore.setPagination({
                    ...applicationStore.Pagination,
                    total: response?.data?.totalCount,
                })
                setPagination({
                    ...pagination,
                    total: response?.data?.totalCount,
                });
            } else {
                Swal.fire({
                    title: "Error",
                    text: "Unable to create the request.",
                    icon: "error",
                    confirmButtonColor: "#44637e",
                });
            }
        } catch (error) {
            console.error("Error in GetArchForm:", error);
            Swal.fire({
                title: "Error",
                text: "An error occurred while fetching the data.",
                icon: "error",
                confirmButtonColor: "#44637e",
            });
            setLoading(false);
        }
    };

    useEffect(() => {
        if (applicationStore?.RequestList?.length <= 0) {
            let data = {
                pageNumber: 1,
                pageSize: 10, searchTerm: filterListInput?.getFieldValue("search"), submissionStatus: applicationStore?.submissionStatusString, submissionDecision: applicationStore?.Pagination?.submissionDecision
            }
            fetchData(data);
        }

    }, [applicationStore?.RequestList]);


    const handleSearch = (event) => {
        const value = event.target.value;
        applicationStore.setPagination({
            ...applicationStore.Pagination,
            pageSize: 10,
            current: 1,
            filter: value
        })
        let data = {
            pageNumber: 1,
            pageSize: 10,
            searchTerm: value,
            field: applicationStore?.field,
            order: applicationStore?.order,
            submissionStatus: applicationStore?.submissionStatusString,
            submissionDecision: applicationStore?.Pagination?.submissionDecision
        }

        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }
        debounceTimer.current = setTimeout(() => {
            fetchData(data);
        }, 1000);

        setSearchTerm(value);

    };
    const handleViewFile = (fileId) => {
        setTimeout(() => {

            window.open(
                `${process.env.REACT_APP_ARCH_API_ENDPOINT}api/Media/view/?fileId=${fileId?.trim()}`,
                "_blank"
            );

        }, 100);
    };
    const handleDownload = (data) => {
        const link = document.createElement("a");
        link.href = `${process.env.REACT_APP_ARCH_API_ENDPOINT}api/Media/download?fileId=${data?.fileId}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    const formatPhoneNumber = (input) => {
        if (!input) return "";
        const cleaned = ("" + input).replace(/\D/g, "").slice(0, 10);
        // Format the cleaned input
        const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
        if (match) {
            return [match[1], match[2], match[3]].filter(Boolean).join("-");
        }
        return input;
    };
    const appealFormChange = (e) => {
        const { id, value } = e.target;
        const formatAndSetValue = (formattedValue) => {
            appealDetails[id] = formattedValue;
            form.setFieldsValue(appealDetails);
            setAppealDetails({ ...appealDetails });
        };
        if (id === "phoneNumber") {
            formatAndSetValue(formatPhoneNumber(value));
        } else {
            appealDetails[id] = value;
            setAppealDetails(appealDetails);
        }

    };
    const handleValuesChange = (changedValues, allValues) => {
    };
    const residents = {
        103096: "145011",
        1200782: "5012102",
        112192: "155269",
        1195639: "445445",
    };
    const handleAppealDetails = async () => { // Add async
        try {
            setLoading(true); // Set loading before the API call
            let residentKey = apiStore?.configApi?.community?.residentKey;
            let request = {
                ResidentKey: apiStore?.configApi?.community?.residentKey?.toString(),
                ResidentContactKey: apiStore?.configApi?.community?.residentContactKey
                    ? apiStore?.configApi?.community?.residentContactKey?.toString()
                    : residents[residentKey?.toString()],
                SubmissionId: selectedSubmissionDetail?.submissionId || null,
                SubmissionBaseId: selectedSubmissionDetail?.baseId || null,
                DayTimePhone: appealDetails?.phoneNumber || "",
                ReasonForAppeal: appealDetails?.reasonForAppeal || "",
                AppealDoc: fileList ? fileList.map(file => file.fileId) : [],
                BlockIds: appealDetails?.selectedBlocks
            };

            const response = await apiStore.FormsApiClient.AddAppealRequest(JSON.stringify(request));

            if (response?.data?.status) {
                setResult(response?.data?.data);
                applicationStore?.setRequestList(response?.data?.data);

                const totalCount = response?.data?.totalCount || 0;

                applicationStore.setPagination({
                    ...applicationStore.Pagination,
                    total: totalCount,
                });

                setPagination({
                    ...pagination,
                    total: totalCount,
                });
                setAppealOpen(false);
                form?.resetFields();
                setFileList([]);
                let data = {
                    pageNumber: 1,
                    pageSize: 10,
                    searchTerm: null,
                    field: applicationStore?.field,
                    order: applicationStore?.order,
                    submissionStatus: applicationStore?.submissionStatusString,
                    submissionDecision: applicationStore?.Pagination?.submissionDecision
                }
                fetchData(data);
                Swal.fire({
                    title: "Success",
                    text: "Appeal request created successfully.",
                    icon: "success",
                    confirmButtonColor: "#44637e",
                });
            } else {
                Swal.fire({
                    title: "Error",
                    text: "Unable to create the request.",
                    icon: "error",
                    confirmButtonColor: "#44637e",
                });
            }
        } catch (error) {
            console.error("Error in handleAppealDetails:", error);
            Swal.fire({
                title: "Error",
                text: "An error occurred while processing the request.",
                icon: "error",
                confirmButtonColor: "#44637e",
            });
        } finally {
            setLoading(false);
        }
    };
    const onFinishFailed = (errorInfo) => {
        console.log("Failed:", errorInfo);
    };
    const handleAppealCancel = () => {
        setAppealOpen(false)
        form?.resetFields();
        setFileList([]);
        setDeniedBlockList([]);
    }

    const handleReset = () => {
        applicationStore.setPagination({
            ...applicationStore.Pagination,
            pageSize: 10,
            current: 1,
            filter: ''
        })
        setSearchTerm("")
        filterListInput?.setFieldsValue({ search: '' })
        let data = {
            pageNumber: 1,
            pageSize: 10, searchTerm: filterListInput?.getFieldValue("search"), field: applicationStore?.field,
            order: applicationStore?.order, submissionStatus: applicationStore?.Pagination?.submissionStatus, submissionDecision: applicationStore?.Pagination?.submissionDecision
        }
        fetchData(data);
    }

    const handleBlockChange = (values) => {
        setAppealDetails((prev) => ({
            ...prev,
            selectedBlocks: values,
        }));
    };
    const blockColumns = [
        {
            title: "Original Submission Type",
            dataIndex: "submissionTypesName",
            width: 250,
            ellipsis: true,
            key: "submissionTypesName",
            render: (text, record) => text || record?.title
        },
        {
            title: "Submission Sub-Type",
            dataIndex: "title",
            key: "title",
            render: (text, record) => text || "-"
        },
        {
            title: "Status",
            dataIndex: "blockActionStatus",
            key: "blockActionStatus",
            ...CommonFuncitons.statusTagColumn(),
        },
        {
            title: "Decision",
            dataIndex: "blockDecision",
            key: "blockDecision",
            ...CommonFuncitons.statusTagColumn({
                isBlank: (record) =>
                    record?.blockActionStatus === CommonFuncitons.SubmissionStatus.Canceled,
            }),
        },
        {
            title: "Decision Reason",
            dataIndex: "decisionReason",
            key: "decisionReason",
            width: 250,
            ellipsis: true,
            render: (text) => text || "-"
        },
        {
            title: "Status Reason",
            dataIndex: "statusReason",
            key: "statusReason",
            width: 250,
            ellipsis: true,
            render: (text) => text || "-"
        },
        {
            title: "Decision Date",
            dataIndex: "decisionDate",
            key: "decisionDate",
            width: 200,
            className: "dynamic-table-nowrap-cell",
            render: (value) => formatDate(value)
        }
    ];


    const handleExpand = (expanded, record) => {
        const key = record.baseId;

        setExpandedRowKeys((prevKeys) => {
            if (expanded) {
                return [...prevKeys, key];
            } else {
                return prevKeys.filter((k) => k !== key);
            }
        });
    };


    useEffect(() => {
        if (Array.isArray(applicationStore?.RequestList)) {
            const defaultExpanded = applicationStore.RequestList
                .filter(
                    (r) =>
                        r.isBlockVotingProccess &&
                        Array.isArray(r.blocks) &&
                        r.blocks.length > 0
                )
                .map((r) => r.baseId);

            setExpandedRowKeys(defaultExpanded);
        }
    }, [applicationStore?.RequestList]);

    return (
        <>
            {loading && <Spinner />}
            <Modal
                title="Appeal Form"
                centered
                footer=""
                open={appealOpen}
                Ok={() => setAppealOpen(false)}
                Cancel={handleAppealCancel}
                width={500}
            >
                {(pageName) => (
                    <>{loading && <Spinner />}
                        <p>Request Number: {selectedSubmissionDetail?.requestNumber}</p>
                        <p>Submitted Date: {formatDate(selectedSubmissionDetail?.submittedDate)}</p>
                        <p>Unit Address : {selectedSubmissionDetail?.unitName}</p>
                        <Form
                            layout="vertical"
                            className="ant-form"
                            labelCol={{
                                span: 24,
                            }}
                            wrapperCol={{
                                span: 24,
                            }}
                            onFinish={handleAppealDetails}
                            onFinishFailed={onFinishFailed}
                            onValuesChange={handleValuesChange}
                            autoComplete="off"
                            initialValues={{}}
                            form={form}
                        >
                            {selectedSubmissionDetail?.isBlockVotingProccess && deniedBlockList?.length > 0 && (
                                <Form.Item
                                    label="Select Group"
                                    name="selectedBlocks"
                                    rules={validationRules.groupSelection}
                                >
                                    <Checkbox.Group
                                        options={deniedBlockList.map((b) => ({
                                            label: b.submissionTypesName,
                                            value: b.blockId,
                                        }))}
                                        value={appealDetails?.selectedBlocks}
                                        onChange={handleBlockChange}
                                    />
                                </Form.Item>
                            )}
                            <TextAreaInput
                                label="Reason For Appeal"
                                name="reasonForAppeal"
                                placeholder="Please provide a reason for appeal."
                                hasFeedback={true}
                                validateTrigger={"onChange"}
                                size="large"
                                // value={appealDetails?.value}
                                onChange={appealFormChange}
                                rules={validationRules.reason}
                            />
                            <TextInput
                                className="user-form-input"
                                name="phoneNumber"
                                label="Daytime Phone"
                                placeholder="Daytime Phone"
                                hasFeedback={true}
                                validateTrigger={"onChange"}
                                size="large"
                                onChange={appealFormChange}
                                rules={validationRules.dayTimePhone}
                            />
                            <p>File Upload</p>
                            <UploadButton
                                fileList={fileList}
                                setFileList={setFileList}
                                getArchFormResponse={selectedSubmissionDetail}
                                appealDoc={true}
                                handleDownload={handleDownload}
                                handleViewFile={handleViewFile}
                            />
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                                <Button
                                    htmlType="submit"
                                    type="primary"
                                    title="Submit"
                                > Submit
                                </Button>
                            </div>
                        </Form >
                    </>)}
            </Modal>
            <Modal
                centered
                footer=""
                open={withdrawOpen}
                Ok={handleWithdrawCancel}
                Cancel={handleWithdrawCancel}
                width={500}
            >
                {() => (
                    <Form
                        layout="vertical"
                        className="ant-form"
                        form={withdrawForm}
                        onFinish={handleWithdrawSubmit}
                        autoComplete="off"
                    >
                        <TextAreaInput
                            label="Reason for Withdrawal"
                            name="withdrawReason"
                            placeholder="Please provide a withdrawal reason."
                            hasFeedback={true}
                            validateTrigger={"onChange"}
                            size="large"
                            rows={4}
                            rules={validationRules.reason}
                        />
                        <div className="withdraw-reason">
                            <Button onClick={handleWithdrawCancel}>Cancel</Button>
                            <Button
                                htmlType="submit"
                                type="primary"
                                loading={withdrawLoading}
                            >
                                Submit
                            </Button>
                        </div>
                    </Form>
                )}
            </Modal>
            <div className="arch-request-page">
                <div className="architectural-header-wrapper">
                    <div className="architectural-header-left-wrap">
                        <Form
                            layout="vertical"
                            className="ant-form"
                            labelCol={{
                                span: 24,
                            }}
                            wrapperCol={{
                                span: 24,
                            }}
                            form={filterListInput}
                        >
                            <TextInput className="search-bar-wrapper"
                                value={searchTerm}
                                onChange={handleSearch}
                                placeholder={"Search here"}
                                name="search"
                                icon={
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M14 14L11.1 11.1M12.6667 7.33333C12.6667 10.2789 10.2789 12.6667 7.33333 12.6667C4.38781 12.6667 2 10.2789 2 7.33333C2 4.38781 4.38781 2 7.33333 2C10.2789 2 12.6667 4.38781 12.6667 7.33333Z"
                                            stroke="#757575"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </svg>}
                            />
                        </Form>
                    </div>
                    <div className="architectural-header-right-wrap">
                        <Button
                            className="refresh-request-button primary-button"
                            label="Refresh"
                            icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g clip-path="url(#clip0_1517_4867)">
                                    <path d="M23 3.99959V9.99959M23 9.99959H17M23 9.99959L18.36 5.63959C17.2853 4.5643 15.9556 3.77879 14.4952 3.35636C13.0348 2.93393 11.4911 2.88834 10.0083 3.22385C8.52547 3.55935 7.1518 4.26502 6.01547 5.27501C4.87913 6.28499 4.01717 7.56637 3.51 8.99959M1 19.9996V13.9996M1 13.9996H7M1 13.9996L5.64 18.3596C6.71475 19.4349 8.04437 20.2204 9.50481 20.6428C10.9652 21.0652 12.5089 21.1108 13.9917 20.7753C15.4745 20.4398 16.8482 19.7342 17.9845 18.7242C19.1209 17.7142 19.9828 16.4328 20.49 14.9996" stroke="#202224" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
                                </g>
                                <defs>
                                    <clipPath id="clip0_1517_4867">
                                        <rect width="24" height="24" fill="white" />
                                    </clipPath>
                                </defs>
                            </svg>}
                            onClick={handleReset}
                        />
                    </div>
                </div>


                <DynamicTable
                    columns={columns}
                    data={applicationStore?.RequestList}
                    tableLayout="fixed"
                    pagination={{
                        current: applicationStore.Pagination?.current,
                        pageSize: applicationStore.Pagination?.pageSize,
                        total: applicationStore.Pagination?.total,
                        showSizeChanger: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} Request`,
                    }}
                    onChange={handleTableChange}
                    rowKey="baseId"
                    onRowClick={handleView}
                    expandable={{
                        expandedRowKeys: expandedRowKeys,
                        onExpand: handleExpand,
                        expandedRowRender: (record) => (
                            <div className="nested-table-wrapper" style={{ marginLeft: "48px" }}>
                                <DynamicTable
                                    columns={blockColumns}
                                    data={record.blocks}
                                    rowKey="blockId"
                                    pagination={false}
                                    tableLayout="fixed"
                                /></div>
                        ),
                        rowExpandable: (record) =>
                            record.isBlockVotingProccess &&
                            Array.isArray(record.blocks) &&
                            record.blocks.length > 0,
                    }}
                />

                <SideBar
                    title={"Edit Request"}
                    centered={"centered"}
                    open={editFormOpen}
                    Ok={EditOk}
                    closable={EditCancel}
                    onClose={EditCancel}
                    width={1800}
                    children={EditForm}
                />
                <SideBar
                    title={"Copy Request"}
                    centered={"centered"}
                    open={copyopen}
                    Ok={CopyOk}
                    closable={CopyCancel}
                    onClose={CopyCancel}
                    width={1800}
                    children={CopyPermissionsFields}
                />
            </div>
        </>
    );
});

export default ArchitecturalRequestList;